// キャリコネクション（グローバルウェイ）API連携で必須になる
// 「希望勤務地」(HOPE_AREA) と「転職希望時期」(HOPE_TURN_TIME_CD) のヘルパー。
// 選択肢（先方コード表）と住所→勤務地コードの判定は varuna（キャリコネの rt_condition の
// additional_dialog_info）から受け取る（useHopeWorkMasters）。ここは受け取ったマスタを引数に取る純関数だけ。
// formatData に保存する値は先方コード（文字列）。ラベルは表示にだけ使う。
// 希望業種・希望職種は既存ビズリーチマスタ（iCd / jCd）を使うのでここには含まない。

export type HopeWorkArea = {
  code: string
  label: string
  // 大分類（HOPE_AREA_L）
  regionCode: string
  regionLabel: string
}

export type HopeTurnTime = {
  code: string
  label: string
}

export type HopeWorkAreaGroup = {
  regionCode: string
  label: string
  options: HopeWorkArea[]
}

/** varuna から受け取ったマスタ。resolved は「送ったテキスト → 先方の勤務地コード（判定不可は null）」 */
export type HopeMasters = {
  workAreas: HopeWorkArea[]
  turnTimes: HopeTurnTime[]
  resolved: Record<string, string | null>
}

// API仕様で配列3つまで（希望勤務地・希望業種・希望職種いずれも同じ）
export const HOPE_SELECT_MAX = 3

// 転職希望時期の既定値。マーケ要望の「すぐに」に相当する先方コード（1ヶ月以内）。
// コード表の差し替えでこの値が無くなった場合は空欄を出さないよう未選択に戻す
const HOPE_TURN_TIME_DEFAULT_CODE = '1'

// コード表差し替え前（2026-08）にプレビューで保存されたラベル文字列 → 先方コード。
// 今のコード表のラベルと一致しない（「すぐに」「未定」、ヶ/か の表記ゆれ）ので、マスタからは導けない
const LEGACY_TURN_TIMES: HopeTurnTime[] = [
  { label: 'すぐに', code: '1' },
  { label: '3ヶ月以内', code: '2' },
  { label: '6ヶ月以内', code: '3' },
  { label: '1年以内', code: '4' },
  { label: '未定', code: '5' }
]

// 旧形式の保存値（都道府県名など）と区別するための、先方コードの形
const CODE_PATTERN = /^\d+$/

function looksLikeHopeCode(value: string): boolean {
  return CODE_PATTERN.test(value)
}

// 大分類ごとにまとめた選択肢（el-option-group 用）
export function groupHopeAreas(areas: HopeWorkArea[]): HopeWorkAreaGroup[] {
  return areas.reduce((groups: HopeWorkAreaGroup[], area) => {
    const last = groups[groups.length - 1]
    if (last && last.regionCode === area.regionCode) {
      last.options.push(area)
    } else {
      groups.push({
        regionCode: area.regionCode,
        label: area.regionLabel,
        options: [area]
      })
    }
    return groups
  }, [])
}

export function isHopeTurnTimeCode(
  turnTimes: HopeTurnTime[],
  code: string
): boolean {
  return turnTimes.some((item) => item.code === code)
}

export function isHopeAreaCode(areas: HopeWorkArea[], code: string): boolean {
  return areas.some((item) => item.code === code)
}

export function resolveHopeTurnTimeDefault(turnTimes: HopeTurnTime[]): string {
  return isHopeTurnTimeCode(turnTimes, HOPE_TURN_TIME_DEFAULT_CODE)
    ? HOPE_TURN_TIME_DEFAULT_CODE
    : ''
}

// 保存値を先方コードに正規化する。コードならそのまま、旧ラベルなら対応コード、
// どちらでもなければ空文字
export function normalizeHopeTurnTime(
  turnTimes: HopeTurnTime[],
  value: string
): string {
  if (!value) {
    return ''
  }
  if (isHopeTurnTimeCode(turnTimes, value)) {
    return value
  }
  const legacy = LEGACY_TURN_TIMES.find((item) => item.label === value)
  return legacy && isHopeTurnTimeCode(turnTimes, legacy.code) ? legacy.code : ''
}

// 保存値の配列を先方コードに正規化する。旧形式の値（都道府県名など）は varuna の判定結果に寄せ、
// 不明値は捨てる。重複も除く。判定結果がまだ無い値があれば null（正規化を見送る）
export function normalizeHopeAreaCodes(
  masters: HopeMasters,
  values: string[]
): string[] | null {
  const result: string[] = []
  for (const value of values) {
    let code: string | null = value
    if (!isHopeAreaCode(masters.workAreas, value)) {
      // 今のマスタに無い数字（廃止されたコードなど）は判定に回さず捨てる
      if (looksLikeHopeCode(value)) continue
      if (!(value in masters.resolved)) return null
      code = masters.resolved[value]
    }
    if (
      code &&
      isHopeAreaCode(masters.workAreas, code) &&
      result.indexOf(code) < 0
    ) {
      result.push(code)
    }
  }
  return result
}

export function hopeAreaLabel(areas: HopeWorkArea[], code: string): string {
  const found = areas.find((item) => item.code === code)
  return found ? found.label : ''
}

/** 住所・旧形式の勤務地のうち、varuna に判定してもらうテキスト */
export function hopeAreaTextsToResolve(
  address: string,
  savedAreas: string[]
): string[] {
  const texts = [
    address.trim(),
    ...savedAreas.filter((v) => !looksLikeHopeCode(v))
  ]
  return texts.filter((text, i) => text && texts.indexOf(text) === i)
}

export type HopeFieldState = {
  hopeWorkAreas: string[]
  hopeAreaAutofilled: boolean
  hopeTurnTime: string
  hopeTurnTimeAutofilled: boolean
}

export type HopeFieldPatch = Partial<HopeFieldState> & {
  // 希望勤務地の初期値に採用した先方コード（注記の表示に使う）
  defaultAreaCode?: string
}

/**
 * 希望勤務地・転職希望時期の正規化と初期値。書き換える項目だけを返す。
 * マスタが無い（読み込み中・失敗）ときは何も返さない＝保存値を触らない。
 * 判定結果がまだ無い旧形式の値・住所がある項目も見送る（あとで判定が届いたら呼び直す）
 */
export function planHopeAreaAndTurnTime(
  masters: HopeMasters | null,
  state: HopeFieldState,
  address: string
): HopeFieldPatch {
  const patch: HopeFieldPatch = {}
  if (!masters || !masters.workAreas.length || !masters.turnTimes.length) {
    return patch
  }

  const areaCodes = normalizeHopeAreaCodes(masters, state.hopeWorkAreas)
  if (areaCodes) {
    let areas = state.hopeWorkAreas
    let areaAutofilled = state.hopeAreaAutofilled
    if (areaCodes.join(',') !== areas.join(',')) {
      patch.hopeWorkAreas = areaCodes
      areas = areaCodes
      if (areaCodes.length === 0) {
        // 空になったら初期値を入れ直せるようにする
        patch.hopeAreaAutofilled = false
        areaAutofilled = false
      }
    }
    // 希望勤務地: 現住所から求めた先方の勤務地コード。住所が判定できなければ、住所が入るまで待つ
    if (!areaAutofilled) {
      if (areas.length > 0) {
        patch.hopeAreaAutofilled = true
      } else {
        const key = address.trim()
        const code = key ? masters.resolved[key] : null
        if (code && isHopeAreaCode(masters.workAreas, code)) {
          patch.hopeWorkAreas = [code]
          patch.hopeAreaAutofilled = true
          patch.defaultAreaCode = code
        }
      }
    }
  }

  const turnTime = normalizeHopeTurnTime(masters.turnTimes, state.hopeTurnTime)
  let turnTimeAutofilled = state.hopeTurnTimeAutofilled
  if (turnTime !== state.hopeTurnTime) {
    patch.hopeTurnTime = turnTime
    if (!turnTime) {
      patch.hopeTurnTimeAutofilled = false
      turnTimeAutofilled = false
    }
  }
  // 転職希望時期: 「1ヶ月以内」（マーケ要望の「すぐに」相当）
  if (!turnTimeAutofilled) {
    if (turnTime) {
      patch.hopeTurnTimeAutofilled = true
    } else {
      const fallback = resolveHopeTurnTimeDefault(masters.turnTimes)
      if (fallback) {
        patch.hopeTurnTime = fallback
        patch.hopeTurnTimeAutofilled = true
      }
    }
  }
  return patch
}

/**
 * 希望業種・職種の一覧の1件目を、画面で直せる値（kibou*）に合わせた新しい一覧。
 * 変える必要が無い（値が空・一覧が空・既に同じ）ときは null
 */
export function syncHopeListHead(
  list: string[],
  code: string
): string[] | null {
  if (!code || list.length === 0 || list[0] === code) return null
  return [code, ...list.slice(1).filter((c) => c !== code)]
}
